import { useState, useEffect } from 'react';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import { emailDomainsApi as domainsApi } from '../../services/api';
import './LazyMarketing.css';

const Domains = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [autoConfigDns, setAutoConfigDns] = useState(true);
  const [addingDomain, setAddingDomain] = useState(false);

  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    setLoading(true);
    try {
      const response = await domainsApi.list();
      // Transform API response to match component structure
      const transformedDomains = (response.domains || []).map(d => ({
        id: d.id,
        domain: d.domain,
        status: d.warmup_status === 'active' ? 'warmup' : d.status,
        healthScore: d.health_score || 0,
        sentToday: d.sent_today || 0,
        dailyLimit: d.daily_limit || 0,
        warmupDay: d.warmup_day || 0,
        warmupComplete: d.warmup_status === 'completed',
        spf: d.spf_verified,
        dkim: d.dkim_verified,
        dmarc: d.dmarc_verified,
        reputation: getReputation(d.health_score)
      }));
      setDomains(transformedDomains);
    } catch (error) {
      console.error('Failed to fetch domains:', error);
    } finally {
      setLoading(false);
    }
  };

  const getReputation = (healthScore) => {
    if (healthScore >= 80) return 'excellent';
    if (healthScore >= 60) return 'good';
    if (healthScore >= 40) return 'building';
    if (healthScore > 0) return 'poor';
    return 'unverified';
  };

  const handleAddDomain = async () => {
    if (!newDomain) return;
    setAddingDomain(true);
    try {
      await domainsApi.create({
        domain: newDomain,
        autoConfigureDns: autoConfigDns,
        mxProvider: 'microsoft'
      });
      await fetchDomains();
      setNewDomain('');
      setShowAddModal(false);
    } catch (error) {
      console.error('Failed to add domain:', error);
      alert(error.message || 'Failed to add domain');
    } finally {
      setAddingDomain(false);
    }
  };

  const handleVerifyDomain = async (domainId) => {
    try {
      await domainsApi.verify(domainId);
      await fetchDomains();
    } catch (error) {
      console.error('Failed to verify domain:', error);
    }
  };

  const handleStartWarmup = async (domainId) => {
    try {
      await domainsApi.startWarmup(domainId);
      await fetchDomains();
    } catch (error) {
      console.error('Failed to start warmup:', error);
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!confirm('Are you sure you want to delete this domain?')) return;
    try {
      await domainsApi.delete(domainId);
      await fetchDomains();
    } catch (error) {
      console.error('Failed to delete domain:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'var(--success)';
      case 'warmup': return 'var(--warning)';
      case 'pending': return 'var(--primary)';
      case 'suspended': return 'var(--danger)';
      default: return 'var(--text-tertiary)';
    }
  };

  const getReputationColor = (rep) => {
    switch (rep) {
      case 'excellent': return 'var(--success)';
      case 'good': return 'var(--success)';
      case 'building': return 'var(--warning)';
      case 'poor': return 'var(--danger)';
      default: return 'var(--text-tertiary)';
    }
  };

  const stats = [
    { label: 'Total Domains', value: domains.length.toString(), change: `${domains.filter(d => d.status === 'active').length} active`, changeType: 'neutral' },
    { label: 'Emails Sent Today', value: domains.reduce((sum, d) => sum + d.sentToday, 0).toLocaleString(), change: 'Across all domains', changeType: 'neutral' },
    { label: 'Avg Health Score', value: Math.round(domains.filter(d => d.healthScore > 0).reduce((sum, d) => sum + d.healthScore, 0) / Math.max(domains.filter(d => d.healthScore > 0).length, 1)) + '%', change: 'Domain health', changeType: 'positive' },
    { label: 'Daily Capacity', value: domains.reduce((sum, d) => sum + d.dailyLimit, 0).toLocaleString(), change: '99/domain max', changeType: 'neutral' },
  ];

  return (
    <div className="lm-page">
      <div className="lm-page-header">
        <div>
          <h1 className="lm-page-title">Email Domains</h1>
          <p className="lm-page-subtitle">Manage sending domains and warmup schedules</p>
        </div>
        <Button variant="primary" icon="plus" onClick={() => setShowAddModal(true)}>
          Add Domain
        </Button>
      </div>

      <div className="lm-stats-row">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} loading={loading} />
        ))}
      </div>

      <div className="lm-section">
        <div className="lm-section-header">
          <h3 className="lm-section-title">Domain List</h3>
          <div className="lm-section-actions">
            <Button variant="secondary" size="sm" icon="refresh" onClick={fetchDomains}>
              Refresh
            </Button>
          </div>
        </div>

        <div className="domains-table">
          <div className="domains-table-header">
            <span>Domain</span>
            <span>Status</span>
            <span>Health</span>
            <span>Sent Today</span>
            <span>Warmup</span>
            <span>DNS</span>
            <span>Actions</span>
          </div>

          {loading ? (
            <div className="loading-state">Loading domains...</div>
          ) : domains.map(domain => (
            <div key={domain.id} className="domains-table-row">
              <div className="domain-info">
                <span className="domain-name">{domain.domain}</span>
                <span className="domain-reputation" style={{ color: getReputationColor(domain.reputation) }}>
                  {domain.reputation}
                </span>
              </div>

              <span className="domain-status" style={{
                background: `${getStatusColor(domain.status)}20`,
                color: getStatusColor(domain.status)
              }}>
                {domain.status}
              </span>

              <div className="domain-health">
                <div className="health-bar">
                  <div
                    className="health-fill"
                    style={{
                      width: `${domain.healthScore}%`,
                      background: domain.healthScore > 70 ? 'var(--success)' :
                                  domain.healthScore > 40 ? 'var(--warning)' : 'var(--danger)'
                    }}
                  />
                </div>
                <span>{domain.healthScore}%</span>
              </div>

              <div className="domain-sent">
                <span className="sent-count">{domain.sentToday}</span>
                <span className="sent-limit">/ {domain.dailyLimit}</span>
              </div>

              <div className="domain-warmup">
                {domain.warmupComplete ? (
                  <span className="warmup-complete"><Icon name="check-circle" /> Complete</span>
                ) : domain.warmupDay > 0 ? (
                  <span className="warmup-progress">Day {domain.warmupDay}/45</span>
                ) : (
                  <span className="warmup-pending">Not started</span>
                )}
              </div>

              <div className="domain-dns">
                <span className={`dns-badge ${domain.spf ? 'valid' : 'invalid'}`} title="SPF">SPF</span>
                <span className={`dns-badge ${domain.dkim ? 'valid' : 'invalid'}`} title="DKIM">DKIM</span>
                <span className={`dns-badge ${domain.dmarc ? 'valid' : 'invalid'}`} title="DMARC">DMARC</span>
              </div>

              <div className="domain-actions">
                {(domain.status === 'pending' || domain.status === 'dns_configured') && (
                  <Button variant="primary" size="sm" onClick={() => handleVerifyDomain(domain.id)}>Verify</Button>
                )}
                {(domain.status === 'verified' || domain.status === 'dns_configured') && !domain.warmupComplete && domain.warmupDay === 0 && (
                  <Button variant="secondary" size="sm" onClick={() => handleStartWarmup(domain.id)}>Start Warmup</Button>
                )}
                {domain.status === 'suspended' && (
                  <Button variant="warning" size="sm">Investigate</Button>
                )}
                <button className="action-btn" title="Settings">
                  <Icon name="settings" />
                </button>
                <button className="action-btn danger" title="Remove" onClick={() => handleDeleteDomain(domain.id)}>
                  <Icon name="trash-2" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Warmup Info Section */}
      <div className="lm-section warmup-info">
        <h3 className="lm-section-title">Domain Warmup Schedule</h3>
        <div className="warmup-timeline">
          <div className="warmup-phase">
            <div className="phase-header">
              <span className="phase-days">Days 1-7</span>
              <span className="phase-limit">10-20 emails/day</span>
            </div>
            <p>Initial warmup phase. Start slow with highly engaged recipients.</p>
          </div>
          <div className="warmup-phase">
            <div className="phase-header">
              <span className="phase-days">Days 8-14</span>
              <span className="phase-limit">20-40 emails/day</span>
            </div>
            <p>Building reputation. Increase volume gradually.</p>
          </div>
          <div className="warmup-phase">
            <div className="phase-header">
              <span className="phase-days">Days 15-30</span>
              <span className="phase-limit">40-70 emails/day</span>
            </div>
            <p>Steady growth. Monitor deliverability closely.</p>
          </div>
          <div className="warmup-phase">
            <div className="phase-header">
              <span className="phase-days">Days 31-45</span>
              <span className="phase-limit">70-99 emails/day</span>
            </div>
            <p>Final phase. Reaching full sending capacity.</p>
          </div>
        </div>
      </div>

      {/* Add Domain Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Domain</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                <Icon name="x" />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Domain Name</label>
                <input
                  type="text"
                  placeholder="e.g., getz101.com"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                />
                <span className="form-hint">Use a dedicated domain for cold outreach (not your main domain)</span>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={autoConfigDns}
                    onChange={(e) => setAutoConfigDns(e.target.checked)}
                  />
                  <span>Auto-configure DNS (SPF, DKIM, DMARC, MX)</span>
                </label>
                <span className="form-hint">Automatically sets up all required DNS records via Netlify</span>
              </div>
              {!autoConfigDns && (
                <div className="dns-instructions">
                  <h4>Required DNS Records</h4>
                  <p>You'll need to manually configure:</p>
                  <ul>
                    <li><strong>SPF</strong> - Sender Policy Framework</li>
                    <li><strong>DKIM</strong> - DomainKeys Identified Mail</li>
                    <li><strong>DMARC</strong> - Domain-based Message Authentication</li>
                    <li><strong>MX</strong> - Mail Exchange records</li>
                  </ul>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={addingDomain}>Cancel</Button>
              <Button variant="primary" onClick={handleAddDomain} disabled={addingDomain || !newDomain}>
                {addingDomain ? 'Adding...' : 'Add Domain'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Domains;
